/****************************************************************************
 * Copyright 2021 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/

import {
  Pile,
  SgContexts,
  checkOverlapping,
  fromSeveralSgroupAddition,
  fromSgroupAction,
  fromSgroupDeletion,
  FunctionalGroup,
  SGroup,
  Pool,
  expandSGroupWithMultipleAttachmentPoint,
  KetcherLogger,
} from 'ketcher-core';

import LassoHelper from './helper/lasso';
import { isEqual } from 'lodash/fp';
import { selMerge } from './select';
import Editor, { Selection } from '../Editor';
import { Tool } from './Tool';
import { filterNotPartOfSuperatomWithoutLabel } from './helper/filterNotInCollapsedSGroup';

const searchMaps = [
  'atoms',
  'bonds',
  'sgroups',
  'functionalGroups',
  'sgroupData',
];

class SGroupTool implements Tool {
  private readonly editor: Editor;
  private readonly lassoHelper: any;
  isNotActiveTool: boolean | undefined;

  constructor(editor) {
    this.editor = editor;
    this.lassoHelper = new LassoHelper(1, editor, null);

    this.checkSelection();
  }

  checkSelection() {
    let selection = this.editor.selection() ?? {};
    const struct = this.editor.render.ctab;
    const molecule = struct.molecule;
    const filteredAtomsAndBonds = filterNotPartOfSuperatomWithoutLabel(
      { atoms: selection.atoms, bonds: selection.bonds },
      molecule,
    );

    selection = {
      ...selection,
      atoms: filteredAtomsAndBonds.atoms,
      bonds: filteredAtomsAndBonds.bonds,
    };

    selection = this.editor.selection(selection) ?? {};
    this.editor.rotateController.rerender();
    this.editor.update(true);

    if (selection.atoms && selection.bonds) {
      const selectedAtoms = this.editor.selection()?.atoms;

      const sgroups: Pool<SGroup> = molecule.sgroups;
      const newSelected: { atoms: Array<any>; bonds: Array<any> } = {
        atoms: [],
        bonds: [],
      };
      let actualSgroupId;
      let atomsResult: Array<number> = [];
      let extraAtoms;
      const functionalGroups = molecule.functionalGroups;
      const result: Array<number> = [];

      const id = sgroups.find((_, sgroup) =>
        isEqual(sgroup.atoms, selectedAtoms),
      );

      if (selectedAtoms && functionalGroups.size) {
        for (const atom of selectedAtoms) {
          const atomId = FunctionalGroup.atomsInFunctionalGroup(
            functionalGroups,
            atom,
          );

          if (atomId == null) {
            extraAtoms = true;
          }

          const atomFromStruct =
            atomId !== null ? struct.atoms.get(atomId)?.a : null;

          if (atomFromStruct) {
            for (const sgId of atomFromStruct.sgs.values()) {
              actualSgroupId = sgId;
            }
          }

          if (
            atomFromStruct &&
            (FunctionalGroup.isAtomInContractedFunctionalGroup(
              atomFromStruct,
              sgroups,
              functionalGroups,
            ) ||
              SGroup.isAtomInContractedSGroup(atomFromStruct, sgroups))
          ) {
            const sgroupAtoms =
              actualSgroupId !== undefined
                ? SGroup.getAtoms(molecule, sgroups.get(actualSgroupId))
                : undefined;
            const sgroupBonds =
              actualSgroupId !== undefined &&
              SGroup.getBonds(molecule, sgroups.get(actualSgroupId));
            if (sgroupAtoms && sgroupBonds && atom === sgroupAtoms[0]) {
              newSelected.atoms.push(...sgroupAtoms);
              newSelected.bonds.push(...sgroupBonds);
            }
          }

          if (atomFromStruct && atomId !== null) {
            atomsResult.push(atomId);
          }
        }
      }
      if (extraAtoms) {
        atomsResult = [];
      }

      if (atomsResult?.length > 0) {
        for (const id of atomsResult) {
          const fgId = FunctionalGroup.findFunctionalGroupByAtom(
            functionalGroups,
            id,
          );

          if (fgId !== null && !result.includes(fgId)) {
            result.push(fgId);
          }
        }
      }

      if (result.length) {
        this.editor.selection(null);
        this.editor.event.removeFG.dispatch({ fgIds: result });
        return;
      }

      SGroupTool.sgroupDialog(this.editor, id);
      this.isNotActiveTool = true;
    }
  }

  mousedown(event) {
    const ci = this.editor.findItem(event, searchMaps);
    const struct = this.editor.render.ctab;
    const sgroups = struct.sgroups;
    const molecule = struct.molecule;
    const functionalGroups = molecule.functionalGroups;
    const atomResult: Array<number> = [];
    const bondResult: Array<number> = [];
    const result: Array<number> = [];

    if (ci?.map === 'atoms' && functionalGroups.size) {
      const atomId = FunctionalGroup.atomsInFunctionalGroup(
        functionalGroups,
        ci.id,
      );
      const atomFromStruct =
        atomId !== null ? struct.atoms.get(atomId)?.a : null;

      if (
        atomFromStruct &&
        atomId !== null &&
        !(
          FunctionalGroup.isAtomInContractedFunctionalGroup(
            atomFromStruct,
            sgroups,
            functionalGroups,
          ) || SGroup.isAtomInContractedSGroup(atomFromStruct, sgroups)
        )
      ) {
        atomResult.push(atomId);
      }
    }

    if (ci?.map === 'bonds' && functionalGroups.size) {
      const bondId = FunctionalGroup.bondsInFunctionalGroup(
        molecule,
        functionalGroups,
        ci.id,
      );
      const bondFromStruct =
        bondId !== null ? struct.bonds.get(bondId)?.b : null;

      if (
        bondFromStruct &&
        bondId !== null &&
        !(
          FunctionalGroup.isBondInContractedFunctionalGroup(
            bondFromStruct,
            sgroups,
            functionalGroups,
          ) || SGroup.isBondInContractedSGroup(bondFromStruct, sgroups)
        )
      ) {
        bondResult.push(bondId);
      }
    }

    if (ci?.map === 'functionalGroups' && functionalGroups.size) {
      const sgroup = sgroups.get(ci.id);

      if (FunctionalGroup.isFunctionalGroup(sgroup?.item)) {
        this.editor.event.removeFG.dispatch({ fgIds: [ci.id] });
        return;
      }
    }

    if (atomResult.length > 0) {
      for (const id of atomResult) {
        const fgId = FunctionalGroup.findFunctionalGroupByAtom(
          functionalGroups,
          id,
        );

        if (fgId !== null && !result.includes(fgId)) {
          result.push(fgId);
        }
      }
      if (result.length > 0) {
        this.editor.event.removeFG.dispatch({ fgIds: result });
        return;
      }
    } else if (bondResult.length > 0) {
      for (const id of bondResult) {
        const fgId = FunctionalGroup.findFunctionalGroupByBond(
          molecule,
          functionalGroups,
          id,
        );

        if (fgId !== null && !result.includes(fgId)) {
          result.push(fgId);
        }
      }

      this.editor.event.removeFG.dispatch({ fgIds: result });
      return;
    }

    if (!ci) {
      //  ci.type == 'Canvas'
      this.lassoHelper.begin(event);
    }
  }

  mousemove(event) {
    if (this.lassoHelper.running(event)) {
      this.editor.selection(this.lassoHelper.addPoint(event));
    } else {
      this.editor.hover(this.editor.findItem(event, searchMaps), null, event);
    }
  }

  mouseleave(event) {
    if (this.lassoHelper.running(event)) {
      this.lassoHelper.end(event);
    }
  }

  private isContractedFunctionalGroupClicked(ci, functionalGroups): boolean {
    return (
      ci?.map === 'functionalGroups' &&
      functionalGroups.size &&
      FunctionalGroup.isContractedFunctionalGroup(ci.id, functionalGroups)
    );
  }

  private processSelectedAtoms(
    selected,
    functionalGroups,
    struct,
    molecule,
    newSelected,
  ) {
    const atomsResult: Array<number> = [];
    let extraAtoms = false;

    if (!selected || !functionalGroups.size || !selected.atoms) {
      return { atomsResult, extraAtoms };
    }

    const sgroups = struct.sgroups;

    for (const atom of selected.atoms) {
      const atomId = FunctionalGroup.atomsInFunctionalGroup(
        functionalGroups,
        atom,
      );

      if (atomId == null) {
        extraAtoms = true;
        continue;
      }

      const atomFromStruct = struct.atoms.get(atomId)?.a;
      if (!atomFromStruct) {
        continue;
      }

      const actualSgroupId = this.getActualSgroupId(atomFromStruct);

      if (
        this.isAtomInContractedGroup(atomFromStruct, sgroups, functionalGroups)
      ) {
        this.addContractedGroupToSelection(
          atom,
          actualSgroupId,
          molecule,
          struct,
          newSelected,
        );
      }

      atomsResult.push(atomId);
    }

    return { atomsResult, extraAtoms };
  }

  private getActualSgroupId(atomFromStruct): number | undefined {
    let actualSgroupId;
    for (const sgId of atomFromStruct.sgs.values()) {
      actualSgroupId = sgId;
    }
    return actualSgroupId;
  }

  private isAtomInContractedGroup(
    atomFromStruct,
    sgroups,
    functionalGroups,
  ): boolean {
    return (
      FunctionalGroup.isAtomInContractedFunctionalGroup(
        atomFromStruct,
        sgroups,
        functionalGroups,
      ) || SGroup.isAtomInContractedSGroup(atomFromStruct, sgroups)
    );
  }

  private addContractedGroupToSelection(
    atom,
    actualSgroupId,
    molecule,
    struct,
    newSelected,
  ) {
    if (actualSgroupId === undefined) {
      return;
    }

    const sgroupAtoms = SGroup.getAtoms(
      molecule,
      struct.sgroups.get(actualSgroupId)?.item,
    );
    const sgroupBonds = SGroup.getBonds(
      molecule,
      struct.sgroups.get(actualSgroupId)?.item,
    );

    if (atom === sgroupAtoms[0]) {
      newSelected.atoms.push(...sgroupAtoms);
      newSelected.bonds.push(...sgroupBonds);
    }
  }

  private processSelectedBonds(selected, functionalGroups, molecule, struct) {
    const bondsResult: Array<number> = [];
    let extraBonds = false;

    if (!selected || !functionalGroups.size || !selected.bonds) {
      return { bondsResult, extraBonds };
    }

    for (const bond of selected.bonds) {
      const bondId = FunctionalGroup.bondsInFunctionalGroup(
        molecule,
        functionalGroups,
        bond,
      );

      if (bondId === null) {
        extraBonds = true;
        continue;
      }

      const bondFromStruct = struct.bonds.get(bondId)?.b;
      if (bondFromStruct) {
        bondsResult.push(bondId);
      }
    }

    return { bondsResult, extraBonds };
  }

  private expandFunctionalGroupSelection(
    atomsResult,
    bondsResult,
    functionalGroups,
    molecule,
    struct,
    newSelected,
  ) {
    this.expandAtomsToFunctionalGroups(
      atomsResult,
      functionalGroups,
      molecule,
      struct,
      newSelected,
    );
    this.expandBondsToFunctionalGroups(
      bondsResult,
      functionalGroups,
      molecule,
      struct,
      newSelected,
    );
  }

  private expandAtomsToFunctionalGroups(
    atomsResult,
    functionalGroups,
    molecule,
    struct,
    newSelected,
  ) {
    if (!atomsResult.length) {
      return;
    }

    atomsResult.forEach((id) => {
      const fgId = FunctionalGroup.findFunctionalGroupByAtom(
        functionalGroups,
        id,
      );

      if (fgId === null) {
        return;
      }

      const sgroupAtoms = SGroup.getAtoms(
        molecule,
        struct.sgroups.get(fgId)?.item,
      );
      newSelected.atoms.push(...sgroupAtoms);
    });
  }

  private expandBondsToFunctionalGroups(
    bondsResult,
    functionalGroups,
    molecule,
    struct,
    newSelected,
  ) {
    if (!bondsResult.length) {
      return;
    }

    bondsResult.forEach((id) => {
      const fgId = FunctionalGroup.findFunctionalGroupByBond(
        molecule,
        functionalGroups,
        id,
      );

      if (fgId === null) {
        return;
      }

      const sgroupBonds = SGroup.getBonds(
        molecule,
        struct.sgroups.get(fgId)?.item,
      );
      newSelected.bonds.push(...sgroupBonds);
    });
  }

  private collectFunctionalGroupIds(
    atomsResult,
    bondsResult,
    extraAtoms,
    extraBonds,
    functionalGroups,
    molecule,
  ): Array<number> {
    if (extraAtoms || extraBonds) {
      return [];
    }

    const result: Array<number> = [];

    this.collectFunctionalGroupIdsFromAtoms(
      atomsResult,
      functionalGroups,
      result,
    );
    this.collectFunctionalGroupIdsFromBonds(
      bondsResult,
      functionalGroups,
      molecule,
      result,
    );

    return result;
  }

  private collectFunctionalGroupIdsFromAtoms(
    atomsResult,
    functionalGroups,
    result: Array<number>,
  ) {
    if (!atomsResult?.length) {
      return;
    }

    for (const id of atomsResult) {
      const fgId = FunctionalGroup.findFunctionalGroupByAtom(
        functionalGroups,
        id,
      );
      if (fgId !== null && !result.includes(fgId)) {
        result.push(fgId);
      }
    }
  }

  private collectFunctionalGroupIdsFromBonds(
    bondsResult,
    functionalGroups,
    molecule,
    result: Array<number>,
  ) {
    if (!bondsResult?.length) {
      return;
    }

    for (const id of bondsResult) {
      const fgId = FunctionalGroup.findFunctionalGroupByBond(
        molecule,
        functionalGroups,
        id,
      );
      if (fgId !== null && !result.includes(fgId)) {
        result.push(fgId);
      }
    }
  }

  private shouldRemoveSingleFunctionalGroup(
    functionalGroupIds: Array<number>,
  ): boolean {
    return functionalGroupIds.length === 1;
  }

  private determineSelection(event, ci, newSelected, molecule) {
    if (this.lassoHelper.running(event)) {
      return this.handleLassoSelection(event, newSelected, molecule);
    }

    return this.handleClickSelection(event, ci);
  }

  private handleLassoSelection(event, newSelected, molecule) {
    const lassoSelection = this.lassoHelper.end(event);
    let selection =
      newSelected.atoms.length > 0
        ? selMerge(lassoSelection, newSelected, false)
        : lassoSelection;

    const filteredAtomsAndBonds = filterNotPartOfSuperatomWithoutLabel(
      { atoms: selection.atoms, bonds: selection.bonds },
      molecule,
    );

    selection = {
      ...selection,
      atoms: filteredAtomsAndBonds.atoms,
      bonds: filteredAtomsAndBonds.bonds,
    };

    this.editor.selection(selection);
    return { id: null, selection };
  }

  private handleClickSelection(event, ci) {
    if (!ci) {
      return { id: null, selection: null };
    }

    this.editor.hover(this.editor.findItem(event, searchMaps), null, event);

    if (ci.map === 'atoms') {
      return { id: null, selection: { atoms: [ci.id] } };
    }

    if (ci.map === 'bonds') {
      const bond = this.editor.render.ctab.bonds.get(ci.id);
      return {
        id: null,
        selection: {
          atoms: [bond?.b.begin, bond?.b.end],
          bonds: [ci.id],
        },
      };
    }

    if (ci.map === 'sgroups' || ci.map === 'sgroupData') {
      return { id: ci.id, selection: null };
    }

    return { id: null, selection: null };
  }

  private shouldOpenDialog(id, selection): boolean {
    const isAtomsOrBondsSelected =
      selection?.atoms?.length || selection?.bonds?.length;
    return id !== null || isAtomsOrBondsSelected;
  }

  mouseup(event) {
    const struct = this.editor.render.ctab;
    const molecule = struct.molecule;
    const functionalGroups = molecule.functionalGroups;
    const ci = this.editor.findItem(event, searchMaps);
    const selected = this.editor.selection();

    if (this.isContractedFunctionalGroupClicked(ci, functionalGroups)) {
      return;
    }

    const newSelected: Record<string, Array<any>> = { atoms: [], bonds: [] };
    const { atomsResult, extraAtoms } = this.processSelectedAtoms(
      selected,
      functionalGroups,
      struct,
      molecule,
      newSelected,
    );
    const { bondsResult, extraBonds } = this.processSelectedBonds(
      selected,
      functionalGroups,
      molecule,
      struct,
    );

    this.expandFunctionalGroupSelection(
      atomsResult,
      bondsResult,
      functionalGroups,
      molecule,
      struct,
      newSelected,
    );

    const functionalGroupIds = this.collectFunctionalGroupIds(
      atomsResult,
      bondsResult,
      extraAtoms,
      extraBonds,
      functionalGroups,
      molecule,
    );

    if (this.shouldRemoveSingleFunctionalGroup(functionalGroupIds)) {
      this.editor.selection(null);
      this.lassoHelper.cancel();
      this.editor.event.removeFG.dispatch({ fgIds: functionalGroupIds });
      return;
    }

    const { id, selection } = this.determineSelection(
      event,
      ci,
      newSelected,
      molecule,
    );

    if (this.shouldOpenDialog(id, selection)) {
      this.editor.selection(selection);
      SGroupTool.sgroupDialog(this.editor, id);
    }
  }

  cancel() {
    if (this.lassoHelper.running()) {
      this.lassoHelper.end();
    }
    this.editor.selection(null);
  }

  static sgroupDialog(editor: Editor, id: number | null) {
    const restruct = editor.render.ctab;
    const struct = restruct.molecule;
    const selection = editor.selection() ?? {};
    const sg = id !== null ? struct.sgroups.get(id) : null;

    // Prevent opening S-Group properties for expanded monomers
    // Editing S-Group properties of expanded monomers can cause data corruption
    // when switching between Molecules and Macromolecules canvas
    if (sg?.isMonomer) {
      return Promise.resolve();
    }

    let attrs;
    if (sg) {
      attrs = sg.getAttrs();
      if (!attrs.context)
        attrs.context = getContextBySgroup(restruct, sg.atoms);
    } else {
      attrs = {
        context: getContextBySelection(restruct, selection),
      };
    }

    const res = editor.event.sgroupEdit.dispatch({
      type: sg?.type,
      attrs,
    });

    return Promise.resolve(res)
      .then((newSg) => {
        // TODO: check before signal
        const isQuerySGroup = SGroup.isQuerySGroup(newSg);
        const isDataSGroup = SGroup.isDataSGroup(newSg);
        if (
          !isDataSGroup && // when data s-group separates
          !isQuerySGroup &&
          checkOverlapping(struct, 'common', selection.atoms)
        ) {
          editor.event.message.dispatch({
            error: 'Partial S-group overlapping is not allowed.',
          });
        } else {
          if (
            !sg &&
            !isDataSGroup &&
            !isQuerySGroup &&
            (!selection.atoms || selection.atoms.length === 0)
          ) {
            return;
          }

          const canReplaceExistingDataSg =
            sg?.getAttrs().context === newSg.attrs.context && !isQuerySGroup;

          if (sg && canReplaceExistingDataSg) {
            const action = fromSeveralSgroupAddition(
              restruct,
              newSg.type,
              sg.atoms,
              newSg.attrs,
            ).mergeWith(fromSgroupDeletion(restruct, id));

            action.mergeWith(expandSGroupWithMultipleAttachmentPoint(restruct));

            editor.update(action);
            editor.selection(selection);
            return;
          }
          const result = isQuerySGroup
            ? createQueryComponentSGroup(id, editor, newSg, selection, sg)
            : fromContextType(id, editor, newSg, selection);

          result && editor.update(result.action);
          editor.selection(null);
        }
      })
      .catch((e) => {
        KetcherLogger.error('sgroup.ts::SGroupTool::sgroupDialog', e);
      });
  }
}

function createQueryComponentSGroup(
  id: number | null,
  editor: Editor,
  newSg,
  selection: Selection,
  sg: SGroup | null | undefined,
) {
  const struct = editor.render.ctab.molecule;
  if (!selection.atoms && sg) {
    if (!sg.atoms) {
      editor.errorHandler?.('Cannot convert to a query component');
      return;
    }
    selection = { atoms: sg.atoms ?? [] };
  }
  if (checkOverlapping(struct, 'queryComponent', selection.atoms)) {
    editor.errorHandler?.(
      'Cannot create a query component: one fragment can only be part of one query component',
    );
  } else {
    return fromContextType(id, editor, newSg, selection);
  }
}

function getContextBySgroup(restruct, sgAtoms) {
  const struct = restruct.molecule;

  if (sgAtoms.length === 1) {
    return SgContexts.Atom;
  }

  if (manyComponentsSelected(restruct, sgAtoms)) {
    return SgContexts.Multifragment;
  }

  if (singleComponentSelected(restruct, sgAtoms)) {
    return SgContexts.Fragment;
  }

  const atomSet = new Pile(sgAtoms);

  const sgBonds = Array.from(struct.bonds.values()).filter(
    (bond: any) => atomSet.has(bond.begin) && atomSet.has(bond.end),
  );

  return anyChainedBonds(sgBonds) ? SgContexts.Group : SgContexts.Bond;
}

function getContextBySelection(restruct, selection) {
  const struct = restruct.molecule;

  if (selection.atoms && !selection.bonds) {
    return SgContexts.Atom;
  }

  const bonds = selection.bonds.map((bondid) => struct.bonds.get(bondid));

  if (!anyChainedBonds(bonds)) {
    return SgContexts.Bond;
  }

  selection.atoms = selection.atoms ?? [];

  const atomSet = new Pile(selection.atoms);
  const allBondsSelected = bonds.every(
    (bond) => atomSet.has(bond.begin) && atomSet.has(bond.end),
  );

  if (singleComponentSelected(restruct, selection.atoms) && allBondsSelected) {
    return SgContexts.Fragment;
  }

  return manyComponentsSelected(restruct, selection.atoms)
    ? SgContexts.Multifragment
    : SgContexts.Group;
}

function fromContextType(id, editor, newSg, currSelection) {
  const restruct = editor.render.ctab;
  const sg = restruct.molecule.sgroups.get(id);
  const sourceAtoms = sg?.atoms ?? currSelection.atoms ?? [];
  const context = newSg.attrs.context;

  if (
    newSg.type === SGroup.TYPES.SUP ||
    newSg.type === SGroup.TYPES.nucleotideComponent
  ) {
    newSg.attrs.expanded = true;
  }

  const result = fromSgroupAction(
    context,
    restruct,
    newSg,
    sourceAtoms,
    currSelection,
  );

  result.selection = result.selection || currSelection;

  if (id !== null && id !== undefined) {
    result.action = result.action.mergeWith(fromSgroupDeletion(restruct, id));
  }

  editor.selection(result.selection);

  return result;
}

function anyChainedBonds(bonds) {
  if (bonds.length === 0) {
    return true;
  }

  for (let i = 0; i < bonds.length; ++i) {
    const fixedBond = bonds[i];
    for (let j = 0; j < bonds.length; ++j) {
      if (i === j) continue;

      const bond = bonds[j];

      if (fixedBond.end === bond.begin || fixedBond.end === bond.end) {
        return true;
      }
    }
  }

  return false;
}

function singleComponentSelected(restruct, atoms) {
  return countOfSelectedComponents(restruct, atoms) === 1;
}

function manyComponentsSelected(restruct, atoms) {
  return countOfSelectedComponents(restruct, atoms) > 1;
}

function countOfSelectedComponents(restruct, atoms): any {
  const atomSet = new Pile(atoms);

  return Array.from(restruct.connectedComponents.values()).reduce(
    (acc: number, component) =>
      acc + (atomSet.isSuperset(component as Pile) ? 1 : 0),
    0,
  );
}

export default SGroupTool;
